import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    Timestamp,
} from 'firebase/firestore';
import { db } from './config';

// Message data interface
export interface Message {
    id?: string;
    name: string;
    email: string;
    subject: string;
    message: string;
    read: boolean;
    archived: boolean;
    important?: boolean;
    replied?: boolean;
    createdAt?: Timestamp;
}

// Collection reference
const messagesCollection = collection(db, 'messages');

/**
 * Get all messages
 */
export const getAllMessages = async (): Promise<Message[]> => {
    try {
        const messagesQuery = query(messagesCollection, orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(messagesQuery);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Message));
    } catch (error) {
        console.error('Error getting messages:', error);
        throw error;
    }
};

/**
 * Get unread messages
 */
export const getUnreadMessages = async (): Promise<Message[]> => {
    try {
        const messagesQuery = query(
            messagesCollection,
            where('read', '==', false),
            orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(messagesQuery);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Message));
    } catch (error) {
        console.error('Error getting unread messages:', error);
        throw error;
    }
};

/**
 * Get a message by ID
 */
export const getMessageById = async (id: string): Promise<Message | null> => {
    try {
        const docRef = doc(db, 'messages', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return {
                id: docSnap.id,
                ...docSnap.data()
            } as Message;
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error getting message:', error);
        throw error;
    }
};

/**
 * Create a new message
 */
export const createMessage = async (messageData: Omit<Message, 'id' | 'createdAt'>): Promise<string> => {
    try {
        const docRef = await addDoc(messagesCollection, {
            ...messageData,
            createdAt: serverTimestamp()
        });

        return docRef.id;
    } catch (error) {
        console.error('Error creating message:', error);
        throw error;
    }
};

/**
 * Mark a message as read
 */
export const markMessageAsRead = async (id: string): Promise<void> => {
    try {
        const docRef = doc(db, 'messages', id);
        await updateDoc(docRef, {
            read: true,
        });
    } catch (error) {
        console.error('Error marking message as read:', error);
        throw error;
    }
};

/**
 * Archive a message
 */
export const archiveMessage = async (id: string): Promise<void> => {
    try {
        const docRef = doc(db, 'messages', id);
        await updateDoc(docRef, {
            archived: true,
        });
    } catch (error) {
        console.error('Error archiving message:', error);
        throw error;
    }
};

/**
 * Unarchive a message
 */
export const unarchiveMessage = async (id: string): Promise<void> => {
    try {
        const docRef = doc(db, 'messages', id);
        await updateDoc(docRef, {
            archived: false,
        });
    } catch (error) {
        console.error('Error unarchiving message:', error);
        throw error;
    }
};

/**
 * Delete a message
 */
export const deleteMessage = async (id: string): Promise<void> => {
    try {
        const docRef = doc(db, 'messages', id);
        await deleteDoc(docRef);
    } catch (error) {
        console.error('Error deleting message:', error);
        throw error;
    }
};

/**
 * Update the importance status of a message
 */
export const updateMessageImportance = async (id: string, isImportant: boolean): Promise<void> => {
    try {
        const docRef = doc(db, 'messages', id);
        await updateDoc(docRef, {
            important: isImportant,
        });
    } catch (error) {
        console.error('Error updating message importance:', error);
        throw error;
    }
};

/**
 * Update the replied status of a message
 */
export const updateMessageRepliedStatus = async (id: string, isReplied: boolean): Promise<void> => {
    try {
        const docRef = doc(db, 'messages', id);
        await updateDoc(docRef, {
            replied: isReplied,
        });
    } catch (error) {
        console.error('Error updating message replied status:', error);
        throw error;
    }
}; 